/** @jsxImportSource @emotion/react */
import {useMemo, useState} from 'react'
import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import {skipToken} from '@reduxjs/toolkit/query/react'
import ReactSelect from 'react-select'

import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import {PhysicianForm} from 'components/organisms/physician/PhysicianForm'
import { physicianFormalName } from 'helpers/nameHelper'
import Input, {TextInput} from 'components/atoms/Input'
import {MaskedTextInput} from 'components/atoms/InputMask'
import {
  useCreateLabPhysicianMutation,
  usePhysiciansLookupQuery,
  useUpdateLabPhysicianMutation,
} from 'store/services/labPhysician'
import {useGetLabOfficesQuery} from 'store/services/office'
import {NeededDeviceType, Physician, PhysicianType, ProviderType} from 'store/types'
import {TooltipWrapper} from 'components/atoms/TooltipWrapper'
import {formatPhoneNumber} from 'helpers/format'
import {confirm} from 'components/molecules/Confirmation'
import states from 'constants/states'
import {toast} from 'react-toastify'
import {isOrganization} from '../../../helpers/followUpProvidersHelper'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  physician?: Physician
  isUpdateMode?: boolean
}

const getPhysicianValidation = (isUpdateMode: boolean) =>
  Yup.object().shape({
    npi: Yup.string().required('NPI is required').length(10, 'NPI must be 10 digits'),
    providerType: Yup.string().required('Provider type is required'),
    firstName: Yup.string().when('providerType', {
      is: (val: string) => val === ProviderType.INDIVIDUAL,
      then: (schema) => schema.required('First name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    lastName: Yup.string().when('providerType', {
      is: (val: string) => val === ProviderType.INDIVIDUAL,
      then: (schema) => schema.required('Last name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    organizationName: Yup.string().when('providerType', {
      is: (val: string) => val === ProviderType.ORGANIZATION,
      then: (schema) => schema.required('Organization name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    telephone: isUpdateMode ? Yup.string() : Yup.string().required('Phone is required'),
    addressStreetLine1: isUpdateMode ? Yup.string() : Yup.string().required('Address is required'),
    addressCity: isUpdateMode ? Yup.string() : Yup.string().required('City is required'),
    addressState: isUpdateMode ? Yup.string() : Yup.string().required('State is required'),
    addressZip: isUpdateMode ? Yup.string() : Yup.string().required('Zip code is required'),
  })

interface FormValues {
  npi: string
  providerType: string
  physicianType: PhysicianType[]
  preferences: string[]
  firstName: string
  lastName: string
  organizationName: string
  credential: string
  telephone: string
  fax: string
  addressStreetLine1: string
  addressStreetLine2: string
  addressCity: string
  addressState: string
  addressZip: string
  comment: string
  officeId: string
  licensedIn: string[]
}

const initialValues: FormValues = {
  npi: '',
  providerType: ProviderType.INDIVIDUAL,
  physicianType: [PhysicianType.REFERRING],
  preferences: [],
  firstName: '',
  lastName: '',
  organizationName: '',
  credential: '',
  telephone: '',
  fax: '',
  addressStreetLine1: '',
  addressStreetLine2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  comment: '',
  officeId: '',
  licensedIn: [],
}

const physicianTypeOptions = [
  { value: PhysicianType.REFERRING, label: 'Referring' },
  { value: PhysicianType.FOLLOW_UP, label: 'Follow-up' },
  { value: PhysicianType.INTERPRETING, label: 'Interpreting' },
]

const physicianPreferenceOptions = Object.entries(NeededDeviceType).map(([key, label]) => ({
  value: key,
  label,
}))

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, zIndex: 10000 }),
}

const splitCsvValues = (value?: string) =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : []

const neededDeviceTypeValueToKey = Object.entries(NeededDeviceType).reduce<Record<string, string>>(
  (acc, [key, value]) => {
    acc[value] = key
    return acc
  },
  {},
)

const normalizePreferenceValues = (value?: string) =>
  splitCsvValues(value).map((item) => neededDeviceTypeValueToKey[item] ?? item)

const isInterpretingOnly = (physicianTypes: PhysicianType[]) =>
  physicianTypes.length === 1 && physicianTypes[0] === PhysicianType.INTERPRETING

export const PhysicianModal = ({
  isOpen,
  onClose,
  onSuccess,
  physician,
  isUpdateMode = false,
}: IProps) => {
  const [createLabPhysician, { isLoading: isCreating }] = useCreateLabPhysicianMutation()
  const [updateLabPhysician, { isLoading: isUpdating }] = useUpdateLabPhysicianMutation()
  const { data: offices } = useGetLabOfficesQuery({})
  const [searchNpi, setSearchNpi] = useState('')
  const [searchLastName, setSearchLastName] = useState('')
  const [searchFirstName, setSearchFirstName] = useState('')
  const [searchState, setSearchState] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedPhysician, setSelectedPhysician] = useState<Physician | null>(null)

  const isLoading = isCreating || isUpdating
  const physicianValidation = useMemo(() => getPhysicianValidation(isUpdateMode), [isUpdateMode])

  const officesOptions = useMemo(
    () =>
      offices?.map((o) => ({
        value: o.id,
        label: `${o.officeName} (${o.addressCity}, ${o.addressState})`,
      })),
    [offices],
  )

  // Search for physicians - requires NPI OR (lastName + state)
  const isSearchValid = searchNpi || (searchLastName?.trim() && searchState)
  const searchParams =
    hasSearched && isSearchValid && !isUpdateMode
      ? {
          npi: searchNpi,
          lastName: searchLastName,
          firstName: searchFirstName,
          addressState: searchState,
        }
      : undefined

  const { data: searchResults, isFetching: isSearching } = usePhysiciansLookupQuery(
    searchParams ?? skipToken,
  )

  const handleClear = () => {
    setSearchNpi('')
    setSearchLastName('')
    setSearchFirstName('')
    setSearchState('')
    setHasSearched(false)
    setSelectedPhysician(null)
  }

  const handleClose = () => {
    handleClear()
    onClose()
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const isValid = searchNpi || (searchLastName?.trim() && searchState)
    if (isValid) {
      setHasSearched(true)
    } else {
      confirm('', {
        title: 'Insufficient Search Criteria',
        description: 'Please enter at least last name and state or NPI.',
        yesAction: 'Ok',
        noAction: '',
      })
    }
  }

  const handleSelectPhysician = (physician: Physician) => {
    setSelectedPhysician(physician)
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const hasInterpretingOnlyType = isInterpretingOnly(values.physicianType)
      const officeId =
        hasInterpretingOnlyType
          ? isUpdateMode
            ? null
            : undefined
          : values.officeId
            ? Number(values.officeId)
            : undefined
      const physicianType = values.physicianType.length
        ? values.physicianType.join(',')
        : undefined
      const preferences = values.preferences.length
        ? values.preferences.join(',')
        : undefined

      if (isUpdateMode && physician) {
        // Update existing physician
        await updateLabPhysician({
          npi: values.npi,
          firstName: values.firstName || undefined,
          lastName: values.lastName || undefined,
          credential:
            values.providerType === ProviderType.INDIVIDUAL ? values.credential.trim() : undefined,
          providerType: values.providerType as ProviderType,
          physicianType,
          preferences,
          organizationName:
            values.providerType === ProviderType.ORGANIZATION ? values.organizationName : undefined,
          officeId,
          licensedIn: values.licensedIn.length ? values.licensedIn.join(',') : undefined,
        }).unwrap()
        handleClose()
        onSuccess()
        toast.success('Physician updated successfully!')
      } else {
        // Create new physician
        await createLabPhysician({
          npi: values.npi,
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          credential: values.credential || undefined,
          providerType: values.providerType as ProviderType,
          physicianType,
          preferences,
          organizationName:
            values.providerType === ProviderType.ORGANIZATION ? values.organizationName : undefined,
          officeId: officeId ?? undefined,
          isFollowUpCareProvider: values.physicianType.includes(PhysicianType.FOLLOW_UP),
          licensedIn: values.licensedIn.length ? values.licensedIn.join(',') : undefined,
          address: {
            telephone: values.telephone,
            fax: values.fax,
            addressStreetLine1: values.addressStreetLine1,
            addressStreetLine2: values.addressStreetLine2,
            addressCity: values.addressCity,
            addressState: values.addressState,
            addressZip: values.addressZip,
            comment: values.comment,
            addressPurpose: 'LOCATION',
          },
        }).unwrap()
        handleClose()
        onSuccess()
        toast.success('Physician added successfully!')
      }
    } catch (error) {
      let message = 'Unknown error occurred'

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error as { data?: { message?: unknown } }
        if (errorData.data?.message && typeof errorData.data.message === 'string') {
          message = errorData.data.message
        } else {
          message = `Unknown error: ${JSON.stringify(error)}`
        }
      }

      toast.error(message)
      console.error('Error with physician:', message)
    }
  }

  const getInitialValues = (): FormValues => {
    if (physician) {
      const address =
        physician.addresses?.find((a) => a.addressPurpose === 'LOCATION') ||
        physician.addresses?.[0]
      return {
        npi: physician.npi || '',
        providerType: physician.providerType || ProviderType.INDIVIDUAL,
        physicianType: splitCsvValues(physician?.physicianType).length
          ? (splitCsvValues(physician?.physicianType) as PhysicianType[])
          : physician.isFollowUpCareEnabled
            ? [PhysicianType.FOLLOW_UP]
            : [PhysicianType.REFERRING],
        preferences: normalizePreferenceValues(physician.preferences),
        firstName: physician.firstName || '',
        lastName: physician.lastName || '',
        organizationName: physician.organizationName || '',
        credential: physician.credential || '',
        telephone: address?.telephone?.replace(/^\+1/, '') || '',
        fax: address?.fax?.replace(/^\+1/, '') || '',
        addressStreetLine1: address?.addressStreetLine1 || '',
        addressStreetLine2: address?.addressStreetLine2 || '',
        addressCity: address?.addressCity || '',
        addressState: address?.addressState || '',
        addressZip: address?.addressZip || '',
        comment: address?.comment || '',
        officeId: physician.officeId?.toString() || '',
        licensedIn: physician?.licensedIn ? physician.licensedIn.split(',').filter(Boolean) : [],
      }
    }
    if (selectedPhysician) {
      const address =
        selectedPhysician.addresses?.find((a) => a.addressPurpose === 'LOCATION') ||
        selectedPhysician.addresses?.[0]
      return {
        npi: selectedPhysician.npi || '',
        providerType: selectedPhysician.providerType || ProviderType.INDIVIDUAL,
        physicianType: [PhysicianType.REFERRING],
        preferences: [],
        firstName: selectedPhysician.firstName || '',
        lastName: selectedPhysician.lastName || '',
        organizationName: selectedPhysician.organizationName || '',
        credential: selectedPhysician.credential || '',
        telephone: address?.telephone?.replace(/^\+1/, '') || '',
        fax: address?.fax?.replace(/^\+1/, '') || '',
        addressStreetLine1: address?.addressStreetLine1 || '',
        addressStreetLine2: address?.addressStreetLine2 || '',
        addressCity: address?.addressCity || '',
        addressState: address?.addressState || '',
        addressZip: address?.addressZip || '',
        comment: address?.comment || '',
        officeId: selectedPhysician.officeId?.toString() || '',
        licensedIn: [],
      }
    }
    return initialValues
  }

  const showNoResults =
    hasSearched && !isSearching && (!searchResults || searchResults.length === 0) && !isUpdateMode
  const showResults =
    hasSearched && !isSearching && searchResults && searchResults.length > 0 && !isUpdateMode

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" centered>
      <Modal.Header>
        <Modal.Title>{isUpdateMode ? 'Update Physician' : 'Add Physician'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!selectedPhysician && !isUpdateMode ? (
          <div>
            {/* Search Form */}
            <form onSubmit={handleSearch}>
              <div className="row mb-3">
                <div className="col-md-2">
                  <Input
                    label="Last Name"
                    placeholder="Last Name"
                    name="searchLastName"
                    value={searchLastName}
                    handleChange={(e) => setSearchLastName(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <Input
                    label="First Name"
                    placeholder="First Name"
                    name="searchFirstName"
                    value={searchFirstName}
                    handleChange={(e) => setSearchFirstName(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <Input
                    label="NPI"
                    placeholder="NPI"
                    name="searchNpi"
                    value={searchNpi}
                    handleChange={(e) => setSearchNpi(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="textGray">State</label>
                  <select
                    className="form-control"
                    value={searchState}
                    onChange={(e) => setSearchState(e.target.value)}>
                    <option value="">All States</option>
                    {states.map((s, i) => (
                      <option key={i} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button type="submit" className="btn btn-primary mr-2" disabled={isSearching}>
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                  <button type="button" className="btn btn-default" onClick={handleClear}>
                    Clear
                  </button>
                </div>
              </div>
            </form>

            {isSearching && (
              <div className="text-center py-4">
                <i className="fa fa-spinner fa-spin mr-2" />
                Searching...
              </div>
            )}

            {showNoResults && (
              <div
                className="text-center py-4 px-3 border rounded"
                css={{ backgroundColor: '#fff' }}>
                <i className="fa fa-search text-muted mr-2" />
                <span className="text-secondary">
                  No physicians found matching your search criteria. Please try a different search.
                </span>
              </div>
            )}

            {showResults && (
              <div className="search-results">
                <h6 className="text-muted mb-2">Search Results ({searchResults.length})</h6>
                {searchResults.map((physician, index) => (
                  <div
                    key={physician.npi + index}
                    className="card mb-2"
                    css={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8f9fa' } }}
                    onClick={() => handleSelectPhysician(physician)}>
                    <div className="card-body py-2">
                      <div className="row align-items-center">
                        <div className="col-md-1">
                          {physician.id ? (
                            <i className="fa fa-user-check text-success" title="In Rolodex" />
                          ) : (
                            <i className="fa fa-user-md text-muted" title="From NPI Registry" />
                          )}
                        </div>
                        <div className="col-md-2">
                          <small className="text-muted">NPI</small>
                          <div>{physician.npi}</div>
                        </div>
                        {isOrganization(physician.providerType) && (
                          <div className="col-md-2">
                            <small className="text-muted">Organization</small>
                            <div>{physician.organizationName}</div>
                          </div>
                        )}
                        <div className="col-md-3">
                          <small className="text-muted">Name</small>
                          <div>
                            {physicianFormalName(physician.firstName, physician.lastName, physician.credential)}
                          </div>
                        </div>
                        <div className={isOrganization(physician.providerType) ? 'col-md-3' : 'col-md-4'}>
                          {physician.addresses?.[0] && (
                            <>
                              <small className="text-muted">Address</small>
                              <div css={{ fontSize: '0.85rem' }}>
                                {physician.addresses[0].addressCity},{' '}
                                {physician.addresses[0].addressState}
                                {physician.addresses[0].telephone && (
                                  <span className="ml-2">
                                    Tel: {formatPhoneNumber(physician.addresses[0].telephone)}
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="col-md-1 text-right">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSelectPhysician(physician)
                            }}>
                            Select
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Formik
            enableReinitialize
            validationSchema={physicianValidation}
            initialValues={getInitialValues()}
            onSubmit={handleSubmit}>
            {({ isValid, values, setFieldValue }) => {
              const isInterpretingPhysician = isInterpretingOnly(values.physicianType)

              return (
                <Form>
                  <div className="container">
                    {selectedPhysician && (
                      <div className="alert alert-success mb-3">
                        <i className="fa fa-check-circle mr-2" />
                        Selected:{' '}
                        {isOrganization(selectedPhysician.providerType)
                          ? `${selectedPhysician.organizationName}, `
                          : ''}
                        {physicianFormalName(selectedPhysician.firstName, selectedPhysician.lastName)} (NPI:{' '}
                        {selectedPhysician.npi})
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-4 form-group">
                        <label className="textGray">NPI *</label>
                        <Field
                          component={MaskedTextInput}
                          name="npi"
                          mask="9999999999"
                          placeholder="NPI"
                          disabled={!!selectedPhysician?.npi || (isUpdateMode && !!physician?.npi)}
                        />
                      </div>
                      <div className="col-4 form-group">
                        <label className="textGray">Provider Type *</label>
                        <select
                          className="form-control"
                          value={values.providerType}
                          onChange={(e) => setFieldValue('providerType', e.target.value)}>
                          <option value={ProviderType.INDIVIDUAL}>Individual</option>
                          <option value={ProviderType.ORGANIZATION}>Organization</option>
                        </select>
                      </div>
                      <div className="col-4 form-group">
                        <label className="textGray">Office</label>
                        <TooltipWrapper
                          show={isInterpretingPhysician}
                          text="The Office cannot be selected for the Interpreting Physician.">
                          <div>
                            <ReactSelect
                              className="basic-single"
                              classNamePrefix="select"
                              isClearable
                              isDisabled={isInterpretingPhysician}
                              isSearchable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              placeholder="Select office"
                              name="officeId"
                              styles={selectStyles}
                              onChange={(item) =>
                                setFieldValue('officeId', item?.value?.toString() || '')
                              }
                              options={officesOptions}
                              value={
                                values.officeId
                                  ? officesOptions?.find(
                                      (o) => Number(o.value) === Number(values.officeId),
                                    )
                                  : null
                              }
                            />
                          </div>
                        </TooltipWrapper>
                      </div>
                    </div>

                    {values.providerType === ProviderType.INDIVIDUAL ? (
                      <div className="row">
                        <div className="col-4 form-group">
                          <label className="textGray">First Name *</label>
                          <Field
                            component={TextInput}
                            label={false}
                            placeholder="First Name"
                            name="firstName"
                            autoComplete="one-time-code"
                          />
                        </div>
                        <div className="col-4 form-group">
                          <label className="textGray">Last Name *</label>
                          <Field
                            component={TextInput}
                            label={false}
                            placeholder="Last Name"
                            name="lastName"
                            autoComplete="one-time-code"
                          />
                        </div>
                        <div className="col-4 form-group">
                          <label className="textGray">Suffix</label>
                          <Field
                            component={TextInput}
                            label={false}
                            placeholder="Suffix"
                            name="credential"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-12 form-group">
                          <label className="textGray">Organization Name *</label>
                          <Field
                            component={TextInput}
                            label={false}
                            placeholder="Organization Name"
                            name="organizationName"
                            autoComplete="one-time-code"
                          />
                        </div>
                      </div>
                    )}

                    {!isUpdateMode && <PhysicianForm />}

                    <div className="row mt-3">
                      <div className="col-4 form-group">
                        <label className="textGray">Physician Type</label>
                        <ReactSelect
                          isMulti
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          placeholder="Select physician type"
                          options={physicianTypeOptions}
                          styles={selectStyles}
                          value={physicianTypeOptions.filter((option) =>
                            values.physicianType.includes(option.value),
                          )}
                          onChange={(selected) => {
                            const nextValue = selected
                              ? (selected.map((option) => option.value) as PhysicianType[])
                              : []
                            setFieldValue('physicianType', nextValue)
                            if (isInterpretingOnly(nextValue)) {
                              setFieldValue('officeId', '')
                            }
                          }}
                        />
                      </div>
                      <div className="col-4 form-group">
                        <label className="textGray">Physician Preferences</label>
                        <ReactSelect
                          isMulti
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          placeholder="Select physician preferences"
                          options={physicianPreferenceOptions}
                          styles={selectStyles}
                          value={physicianPreferenceOptions.filter((option) =>
                            values.preferences.includes(option.value),
                          )}
                          onChange={(selected) =>
                            setFieldValue(
                              'preferences',
                              selected ? selected.map((option) => option.value) : [],
                            )
                          }
                        />
                      </div>
                      <div className="col-4 form-group">
                        <label className="textGray">Licensed In</label>
                        <ReactSelect
                          isMulti
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          placeholder="Select states..."
                          styles={selectStyles}
                          options={states
                            .filter((s) => s.id)
                            .map((s) => ({ value: s.id, label: s.name }))}
                          value={values.licensedIn.map((code) => {
                            const s = states.find((st) => st.id === code)
                            return { value: code, label: s?.name ?? code }
                          })}
                          onChange={(selected) =>
                            setFieldValue(
                              'licensedIn',
                              selected ? selected.map((o) => o.value) : [],
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer mt-4">
                    <Button variant="secondary" onClick={handleClose}>
                      Cancel
                    </Button>
                    {!isUpdateMode && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setSelectedPhysician(null)
                        }}>
                        Back to Search
                      </Button>
                    )}
                    <Button variant="primary" type="submit" disabled={!isValid || isLoading}>
                      {isLoading
                        ? isUpdateMode
                          ? 'Updating...'
                          : 'Adding...'
                        : isUpdateMode
                          ? 'Update Physician'
                          : 'Add Physician'}
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  )
}
